/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

export const JPEG_QUALITY = 0.92
export const STICKER_SIZE_RATIO = 0.3
export const COVER_WIDTH_RATIO = 0.24
export const COVER_HEIGHT_RATIO = 0.3
export const EMOJI_SIZE_RATIO = 0.28
export const MIN_OVERLAY_WIDTH_RATIO = 0.08
export const MAX_OVERLAY_WIDTH_RATIO = 0.9
export const MIN_OVERLAY_HEIGHT_RATIO = 0.05
export const MAX_OVERLAY_HEIGHT_RATIO = 0.95
export const MIN_CROP_RATIO = 0.1
export const RESIZE_HANDLE_SIZE_PX = 28
export const ROTATE_HANDLE_SIZE_PX = 36
export const CROP_HANDLE_SIZE_PX = 28
export const ORIENTED_PREVIEW_MAX_DIMENSION = 2048
export const ORIENTED_EXPORT_MAX_DIMENSION = 4096
export const FILE_EXTENSION_PATTERN = /\.[^.]+$/

export const getCoverBlurPx = (width: number, height: number) => Math.max(4, Math.min(width, height) * 0.05)

export const getCoverPixelCellPx = (width: number, height: number) =>
  Math.max(8, Math.round(Math.min(width, height) / 10))

export const getCoverEffectFilter = (photoFilterCss: string, blurPx: number) =>
  photoFilterCss === 'none' ? `blur(${blurPx}px)` : `${photoFilterCss} blur(${blurPx}px)`

export const PRIVACY_EMOJIS = ['😎', '🙈', '😏', '😈', '❤️', '🍑', '🍆', '🍒', '👄'] as const
export const DEFAULT_PRIVACY_EMOJI = PRIVACY_EMOJIS[0]

export type EditorTool = 'adjust' | 'crop' | 'stickers' | 'blur'
export type PhotoFilter = 'original' | 'mono' | 'warm' | 'cool' | 'fade'
export type CropAspect = 'free' | '1:1' | '4:5' | '16:9'

export const PHOTO_FILTERS: PhotoFilter[] = ['original', 'mono', 'warm', 'cool', 'fade']

export const getPhotoFilterCss = (filter: PhotoFilter) => {
  if (filter === 'mono') {
    return 'grayscale(1)'
  }
  if (filter === 'warm') {
    return 'sepia(0.4) saturate(1.25) hue-rotate(-15deg) contrast(1.05)'
  }
  if (filter === 'cool') {
    return 'saturate(0.85) hue-rotate(20deg) brightness(1.05) contrast(1.05)'
  }
  if (filter === 'fade') {
    return 'contrast(0.8) saturate(0.6) brightness(1.12)'
  }
  return 'none'
}
export type CropHandle = 'nw' | 'ne' | 'sw' | 'se'
export type StickerKind = 'logo' | 'pineapple' | 'flamingo' | 'swinger'
export type CoverKind = 'blur' | 'pixelate' | 'emoji'
export type CoverShape = 'ellipse' | 'rounded-rect'
export type OverlayLayer = 'sticker' | 'cover'

export const CROP_ASPECT_RATIO: Record<Exclude<CropAspect, 'free'>, number> = {
  '1:1': 1,
  '4:5': 4 / 5,
  '16:9': 16 / 9,
}

export const FULL_CROP = {
  x: 0,
  y: 0,
  width: 1,
  height: 1,
} as const

export type CropRect = {
  x: number
  y: number
  width: number
  height: number
}

export type OverlayBase = {
  id: string
  x: number
  y: number
  width: number
  height: number
  rotation: number
}

export type EditorSticker = OverlayBase & {
  kind: StickerKind
  flipX: boolean
}

export type EditorCover = OverlayBase & {
  kind: CoverKind
  shape: CoverShape
  emoji: string
}

export type ImageRect = {
  left: number
  top: number
  width: number
  height: number
}

export type ImageSize = {
  width: number
  height: number
}
